<template>
  <section class="relative bg-yellow-400 pt-16 pb-8 tablet:pb-16">
    <div class="grid-container">
      <div class="col-span-12">
        <div class="mb-16 flex flex-col justify-center gap-x-1 gap-y-2 tablet:mb-20 tablet:flex-row tablet:items-center">
          <label
            v-for="{ label, price, value } in offerTypes"
            :id="value"
            :key="value"
            class="flex cursor-pointer items-center justify-between border border-black p-2.5 font-bold italic transition-colors duration-150 hover:bg-black hover:text-white"
            :class="value === offerType && 'bg-black text-white'"
          >
            <div class="mr-2">
              <input v-model="offerType" class="mr-1" type="radio" :value="value" />

              {{ label }}
            </div>

            <div class="text-xs font-normal italic tablet:text-sm">
              {{ `${price}€` }}
            </div>
          </label>
        </div>

        <div
          class="relative mb-8 flex gap-x-4 overflow-x-auto pt-10 tablet:mb-11 laptop:items-center laptop:justify-center laptop:gap-x-0 laptop:overflow-hidden"
        >
          <div class="offer starter">
            <div class="price">
              {{ `${basePrice}€` }}
            </div>
            <h3 class="text-headline-l">
              {{ $t('new_job_landing.package.starter') }}
            </h3>
            <div class="period">
              {{ $t('new_job_landing.package.days_runtime', { days: 30 }) }}
            </div>

            <ul>
              <li>{{ $t('new_job_landing.bullet.starter_1') }}</li>
              <li>{{ $t('new_job_landing.bullet.starter_2') }}</li>
            </ul>

            <img src="~/assets/images/starter.png" />

            <NuxtLink
              :to="jobsHrPaths.createJob({ locale }, { offerType, offerLevel: JOB_OFFER_LEVEL.STARTER })"
              class="btn-primary-color-m absolute right-10 bottom-10 left-10 items-center justify-center"
            >
              {{ $t('new_job_landing.create_job') }}
            </NuxtLink>
          </div>

          <div class="offer basic highlighted">
            <div class="price">
              <span>{{ `${pricing.basic + basePrice}€` }}</span>
            </div>
            <h3 class="text-headline-l">
              {{ $t('new_job_landing.package.basic') }}
            </h3>
            <div class="period">
              {{ $t('new_job_landing.package.days_runtime', { days: 30 }) }}
            </div>

            <ul>
              <li>
                <span
                  >{{ $t('new_job_landing.package.all_features_from') }} <strong>{{ $t('new_job_landing.package.starter') }}</strong></span
                >
              </li>
              <li>{{ $t('new_job_landing.bullet.basic_1') }}</li>
              <li>{{ $t('new_job_landing.bullet.basic_2') }}</li>
            </ul>

            <img src="~/assets/images/basic.png" />

            <NuxtLink
              :to="jobsHrPaths.createJob({ locale }, { offerType, offerLevel: JOB_OFFER_LEVEL.BASIC })"
              class="btn-primary-color-m absolute right-10 bottom-10 left-10 items-center justify-center"
            >
              {{ $t('new_job_landing.create_job') }}
            </NuxtLink>
          </div>

          <div class="offer premium">
            <div class="price">
              <span>{{ `${pricing.full + basePrice}€` }}</span>
            </div>
            <h3 class="text-headline-l">
              {{ $t('new_job_landing.package.premium') }}
            </h3>
            <div class="period">
              {{ $t('new_job_landing.package.days_runtime', { days: 60 }) }}
            </div>

            <ul>
              <li>
                <span
                  >{{ $t('new_job_landing.package.all_features_from') }} <strong>{{ $t('new_job_landing.package.basic') }}</strong></span
                >
              </li>
              <li>{{ $t('new_job_landing.bullet.premium_1') }}</li>
              <li>{{ $t('new_job_landing.bullet.premium_2') }}</li>
              <li>{{ $t('new_job_landing.bullet.premium_3') }}</li>
            </ul>

            <img src="~/assets/images/premium.png" />

            <NuxtLink
              :to="jobsHrPaths.createJob({ locale }, { offerType, offerLevel: JOB_OFFER_LEVEL.PREMIUM })"
              class="btn-primary-color-m absolute right-10 bottom-10 left-10 items-center justify-center"
            >
              {{ $t('new_job_landing.create_job') }}
            </NuxtLink>
          </div>
        </div>

        <img
          src="~/assets/images/cursor-army2.png"
          class="absolute -bottom-8 left-1/2 hidden w-full max-w-4xl -translate-x-1/2 tablet:block"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { jobsHrPaths } from '@ramp106/omrjs-routing'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { JOB_OFFER_LEVEL, JOB_OFFER_TYPE } from '@/helpers/constants'

import { NuxtLink } from '#components'
import { useLazyAsyncData } from '#imports'
import { useJobsSdk } from '~/gql'

const { locale, t } = useI18n()
const { getPricing } = useJobsSdk()

const offerType = ref(JOB_OFFER_TYPE.STANDARD)
const pricing = computed(() => {
  // Default pricing makes no sense :cry:
  return (
    data.value || {
      standard: 0,
      internship: 0,
      basic: 0,
      full: 0,
    }
  )
})

const basePrice = computed(() => {
  return isStandardOffer.value ? pricing.value.standard : pricing.value.internship
})

const isStandardOffer = computed(() => {
  return offerType.value === JOB_OFFER_TYPE.STANDARD
})

const offerTypes = computed(() => [
  {
    value: JOB_OFFER_TYPE.STANDARD,
    label: t('new_job_landing.offer_type.standard'),
    price: pricing.value.standard,
  },
  {
    value: JOB_OFFER_TYPE.INTERNSHIP,
    label: t('new_job_landing.offer_type.internship'),
    price: pricing.value.internship,
  },
])

const fetchPricing = async () => {
  const res = await getPricing()
  return res.data?.pricing
}

const { data } = useLazyAsyncData('features', async () => await fetchPricing())
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.offer {
  @apply relative h-[374px] w-[276px] shrink-0 bg-white p-8 text-sm laptop:h-[400px] laptop:w-[376px] laptop:p-10 laptop:text-base;

  img {
    @apply absolute -top-16 right-0 h-44 w-44;
  }

  &.highlighted {
    @apply z-10 laptop:h-[450px] laptop:shadow-[0_15px_15px_-15px_rgba(78,90,113,0.3),-10px_0_25px_-20px_rgba(78,90,113,0.5),10px_0_25px_-20px_rgba(78,90,113,0.5)];
  }

  .price {
    @apply text-[22px] font-bold italic;
  }

  h3 {
    @apply mb-3;
  }

  .period {
    @apply mb-8 text-sm tablet:text-base;
  }

  ul {
    @apply list-none pl-4;

    li {
      @apply before:-ml-4 before:inline-block before:w-4 before:font-bold before:content-['\2022'];
    }
  }

  &.starter {
    .price,
    .period {
      @apply text-yellow;
    }

    ul {
      li {
        &::before {
          @apply text-yellow;
        }
      }
    }
  }

  &.basic {
    .price,
    .period {
      @apply text-[#e83e8c];
    }

    ul {
      li {
        &::before {
          @apply text-[#e83e8c];
        }
      }
    }
  }

  &.premium {
    .price,
    .period {
      @apply text-mint;
    }

    ul {
      li {
        &::before {
          @apply text-mint;
        }
      }
    }
  }
}
</style>
